import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Shield,
    UserPlus,
    Trash2,
    Loader2,
    Mail,
    Lock,
    AlertCircle,
    CheckCircle2,
    User,
    Calendar,
    ShieldAlert
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AuthUser {
    id: string;
    email: string;
    created_at: string;
    last_sign_in_at: string | null;
}

const UsersManager: React.FC = () => {
    const { theme } = useOutletContext<{ theme: 'light' | 'dark' }>();
    const isDark = theme === 'dark';

    const [users, setUsers] = useState<AuthUser[]>([]);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Feedback notifications
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // Modal state for confirmation
    const [userToDelete, setUserToDelete] = useState<AuthUser | null>(null);

    // Password validation rules (at least 6 characters, upper, lower, number, symbol)
    const reqLength = password.length >= 6;
    const reqUppercase = /[A-Z]/.test(password);
    const reqLowercase = /[a-z]/.test(password);
    const reqNumber = /[0-9]/.test(password);
    const reqSymbol = /[!@#$%^&*(),.?":{}|<>\-_]/.test(password);
    const isPasswordValid = reqLength && reqUppercase && reqLowercase && reqNumber && reqSymbol;

    const EDGE_FUNCTION_URL = 'https://iuixlsqrlfkfnvnckgij.supabase.co/functions/v1/manage-users';

    // Fetch user list and current user details
    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('No hay una sesión activa');

            setCurrentUser(session.user);

            const res = await fetch(EDGE_FUNCTION_URL, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'No se pudo obtener la lista de usuarios');
            }

            setUsers(data.users || []);
        } catch (err: any) {
            console.error('Error fetching users:', err);
            showNotification(err.message || 'Error al cargar usuarios', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            showNotification('Por favor completa todos los campos', 'error');
            return;
        }

        if (!isPasswordValid) {
            showNotification('La contraseña debe cumplir con todos los requisitos de seguridad (mínimo 6 caracteres, mayúscula, minúscula, número y símbolo)', 'error');
            return;
        }

        setActionLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('No hay una sesión activa');

            const res = await fetch(EDGE_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({ email: email.trim(), password })
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Error al crear el usuario');
            }

            showNotification('Usuario administrativo creado con éxito', 'success');
            setEmail('');
            setPassword('');
            fetchUsers(); // Refresh list
        } catch (err: any) {
            showNotification(err.message || 'Error al crear usuario', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        setActionLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('No hay una sesión activa');

            const res = await fetch(`${EDGE_FUNCTION_URL}?userId=${userToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Error al eliminar el usuario');
            }

            showNotification('El usuario ha sido eliminado correctamente', 'success');
            setUserToDelete(null);
            fetchUsers(); // Refresh list
        } catch (err: any) {
            showNotification(err.message || 'Error al eliminar usuario', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'Nunca';
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-8 max-w-6xl mx-auto">
            {/* Header section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h2 className={`font-title text-2xl uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Gestionar Accesos
                    </h2>
                    <p className="text-[10px] text-[#00d4ff] font-bold uppercase tracking-widest mt-1">
                        Control administrativo de usuarios y contraseñas de acceso al Backoffice
                    </p>
                </div>
            </div>

            {/* Notification popup */}
            {notification && (
                <div className={`p-4 rounded-[10px] border flex gap-3 items-center animate-in fade-in slide-in-from-top-2 duration-300 ${notification.type === 'success'
                        ? 'bg-green-500/10 border-green-500/30 text-green-400'
                        : 'bg-red-500/10 border-red-500/30 text-red-400'
                    }`}>
                    {notification.type === 'success' ? (
                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                    ) : (
                        <AlertCircle className="w-5 h-5 shrink-0" />
                    )}
                    <span className="text-xs font-bold uppercase tracking-wider">{notification.message}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Form to create user */}
                <div className={`rounded-[15px] border p-6 lg:p-8 space-y-6 ${isDark ? 'bg-[#1a1512]/60 border-rock' : 'bg-white border-gray-200 shadow-md'
                    }`}>
                    <div className="space-y-1.5">
                        <h3 className={`font-title text-sm uppercase font-bold tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Crear Nuevo Acceso
                        </h3>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">
                            Establece el email y clave para el nuevo administrador
                        </p>
                    </div>

                    <form onSubmit={handleCreateUser} className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <label className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500 block">
                                Correo Electrónico
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="email"
                                    required
                                    className={`w-full text-xs pl-11 pr-4 py-3 outline-none transition-all rounded-[10px] border ${isDark
                                            ? 'bg-black/60 border-rock text-white focus:border-[#00d4ff]'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#00d4ff] focus:bg-white'
                                        }`}
                                    placeholder="correo@fisuraexpeditions.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    disabled={actionLoading}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-500 block">
                                Contraseña
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="password"
                                    required
                                    className={`w-full text-xs pl-11 pr-4 py-3 outline-none transition-all rounded-[10px] border ${isDark
                                            ? 'bg-black/60 border-rock text-white focus:border-[#00d4ff]'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#00d4ff] focus:bg-white'
                                        }`}
                                    placeholder="Contraseña segura"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    disabled={actionLoading}
                                />
                            </div>

                            {/* Password Requirements Checklist */}
                            {password.length > 0 && (
                                <div className={`p-3.5 rounded-[10px] border space-y-2 mt-2.5 transition-all ${isDark ? 'bg-black/40 border-rock/40' : 'bg-gray-50 border-gray-150 shadow-inner'
                                    }`}>
                                    <p className={`text-[8.5px] uppercase tracking-wider font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Requisitos de Contraseña:
                                    </p>
                                    <ul className="space-y-1.5 text-[10px] font-medium">
                                        <li className={`flex items-center gap-2 transition-colors ${reqLength ? 'text-green-500 font-bold' : 'text-red-400/80'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full transition-all ${reqLength ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-400'}`} />
                                            <span>Al menos 6 caracteres (Largo: {password.length})</span>
                                        </li>
                                        <li className={`flex items-center gap-2 transition-colors ${reqUppercase ? 'text-green-500 font-bold' : 'text-red-400/80'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full transition-all ${reqUppercase ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-400'}`} />
                                            <span>Al menos una letra mayúscula</span>
                                        </li>
                                        <li className={`flex items-center gap-2 transition-colors ${reqLowercase ? 'text-green-500 font-bold' : 'text-red-400/80'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full transition-all ${reqLowercase ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-400'}`} />
                                            <span>Al menos una letra minúscula</span>
                                        </li>
                                        <li className={`flex items-center gap-2 transition-colors ${reqNumber ? 'text-green-500 font-bold' : 'text-red-400/80'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full transition-all ${reqNumber ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-400'}`} />
                                            <span>Al menos un número</span>
                                        </li>
                                        <li className={`flex items-center gap-2 transition-colors ${reqSymbol ? 'text-green-500 font-bold' : 'text-red-400/80'
                                            }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full transition-all ${reqSymbol ? 'bg-green-500 shadow-[0_0_8px_#22c55e]' : 'bg-red-400'}`} />
                                            <span>Al menos un símbolo (ej: @, $, !, %, -)</span>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={actionLoading || !email.trim() || !password.trim() || !isPasswordValid}
                            className={`w-full text-black py-3.5 mt-4 font-bold text-xs uppercase tracking-[0.2em] rounded-[10px] hover:bg-white transition-all shadow-md flex items-center justify-center gap-2 ${(actionLoading || !email.trim() || !password.trim() || !isPasswordValid)
                                    ? 'bg-[#00d4ff]/40 text-black/40 cursor-not-allowed shadow-none'
                                    : 'bg-[#00d4ff] hover:shadow-lg hover:shadow-[#00d4ff]/20'
                                }`}
                        >
                            {actionLoading ? (
                                <Loader2 className="animate-spin w-4 h-4" />
                            ) : (
                                <UserPlus className="w-4 h-4" />
                            )}
                            <span>Crear Cuenta</span>
                        </button>
                    </form>
                </div>

                {/* Table containing current users */}
                <div className={`lg:col-span-2 rounded-[15px] border p-6 lg:p-8 space-y-6 ${isDark ? 'bg-[#1a1512]/60 border-rock' : 'bg-white border-gray-200 shadow-md'
                    }`}>
                    <div className="space-y-1.5">
                        <h3 className={`font-title text-sm uppercase font-bold tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Usuarios con Acceso Activo
                        </h3>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold">
                            Lista de cuentas registradas en el sistema de autenticación
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-500">
                            <Loader2 className="w-8 h-8 text-[#00d4ff] animate-spin" />
                            <span className="text-[10px] font-bold uppercase tracking-widest">Cargando cuentas...</span>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-16 text-gray-500 border border-dashed rounded-[10px] border-rock/30">
                            <p className="text-xs uppercase font-bold tracking-wider">No se encontraron cuentas activas</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border rounded-[10px] border-rock/30">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className={`border-b text-[9px] uppercase font-bold tracking-wider ${isDark ? 'bg-black/40 border-rock text-gray-400' : 'bg-gray-50 border-gray-150 text-gray-500'
                                        }`}>
                                        <th className="p-4">Administrador</th>
                                        <th className="p-4">Fecha Creación</th>
                                        <th className="p-4">Último Ingreso</th>
                                        <th className="p-4 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDark ? 'divide-rock/30 bg-black/10' : 'divide-gray-100'}`}>
                                    {users.map((usr) => {
                                        const isSelf = currentUser && usr.id === currentUser.id;
                                        return (
                                            <tr key={usr.id} className={`${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                                                }`}>
                                                <td className="p-4 font-bold flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${isSelf
                                                            ? 'bg-[#00d4ff]/20 text-[#00d4ff] border border-[#00d4ff]/40'
                                                            : isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        <User className="w-3.5 h-3.5" />
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <span className="truncate">{usr.email}</span>
                                                        {isSelf && (
                                                            <span className="text-[7.5px] font-black uppercase tracking-widest text-[#00d4ff] mt-0.5">
                                                                (Tu Sesión Activa)
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4 font-medium">
                                                    <div className="flex items-center gap-1.5 text-gray-550">
                                                        <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                                                        <span>{formatDate(usr.created_at)}</span>
                                                    </div>
                                                </td>
                                                <td className="p-4 font-medium">
                                                    <span>{formatDate(usr.last_sign_in_at || '')}</span>
                                                </td>
                                                <td className="p-4 text-center">
                                                    <button
                                                        onClick={() => setUserToDelete(usr)}
                                                        disabled={isSelf || actionLoading}
                                                        className={`p-2 rounded-[8px] transition-all ${isSelf
                                                                ? 'opacity-30 cursor-not-allowed text-gray-500'
                                                                : 'hover:bg-red-500/10 text-red-500/80 hover:text-red-500'
                                                            }`}
                                                        title={isSelf ? 'No puedes eliminarte a ti mismo' : 'Eliminar acceso'}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom confirm deletion dialog portal/modal */}
            {userToDelete && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/75 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`w-full max-w-md p-6 lg:p-8 rounded-[20px] border shadow-2xl space-y-6 ${isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-200 text-gray-900'
                        }`}>
                        <div className="flex gap-4 items-start">
                            <div className="p-3 bg-red-500/10 rounded-full border border-red-500/30 text-red-500 shrink-0">
                                <ShieldAlert className="w-6 h-6" />
                            </div>
                            <div className="space-y-1.5">
                                <h4 className="font-title text-sm uppercase font-bold tracking-wider text-red-500">
                                    ¿Confirmar Eliminación?
                                </h4>
                                <p className="text-xs text-gray-500 leading-relaxed font-bold uppercase tracking-wider">
                                    Estás a punto de revocar el acceso de administrador al correo:
                                </p>
                                <p className={`text-xs font-mono font-black break-all py-1.5 px-3 rounded-[6px] ${isDark ? 'bg-black/45 text-[#00d4ff]' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {userToDelete.email}
                                </p>
                            </div>
                        </div>

                        <p className="text-xs text-gray-500 leading-relaxed pt-2">
                            Esta acción es irreversible y el usuario será desconectado inmediatamente del Backoffice, perdiendo la capacidad de gestionar expediciones o reservas.
                        </p>

                        <div className="flex gap-3 justify-end pt-4">
                            <button
                                onClick={() => setUserToDelete(null)}
                                disabled={actionLoading}
                                className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-[8px] transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white' : 'bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteUser}
                                disabled={actionLoading}
                                className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-[8px] transition-all flex items-center gap-2 shadow-lg"
                            >
                                {actionLoading ? (
                                    <Loader2 className="animate-spin w-3.5 h-3.5" />
                                ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                )}
                                <span>Eliminar Acceso</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersManager;
